import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["display", "input"]
  static values = {
    fieldPath: String,
    fieldType: String,
    originalValue: String,
    precision: Number,
    readOnly: Boolean,
    selectOptions: Array
  }

  connect() {
    console.log(`Editable cell controller connected for ${this.fieldPathValue}`)

    this.isEditing = false
    this.currentValue = this.originalValueValue
    this.sheetController = this.findSheetController()

    // Set up click handler for editing
    if (!this.readOnlyValue) {
      this.element.addEventListener('click', this.edit.bind(this))
      this.element.classList.add('cursor-pointer', 'hover:bg-gray-100', 'transition-colors', 'duration-200')
    }
  }

  disconnect() {
    if (this.inputElement) {
      this.inputElement.remove()
    }
  }

  edit() {
    if (this.readOnlyValue || this.isEditing) return

    console.log(`Editing cell: ${this.fieldPathValue}`)

    this.isEditing = true
    this.element.classList.add('editing')

    // Create and show input element
    this.createInputElement()
    this.showInput()
    this.inputElement.focus()
    this.inputElement.select()
  }

  createInputElement() {
    if (this.fieldTypeValue === 'select') {
      this.inputElement = document.createElement('select')
      this.inputElement.className = 'w-full bg-white border-2 border-blue-400 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-600'
      
      // Add options
      const options = this.selectOptionsValue || []
      options.forEach(option => {
        const optionElement = document.createElement('option')
        optionElement.value = option
        optionElement.textContent = option
        optionElement.selected = option === this.currentValue
        this.inputElement.appendChild(optionElement)
      })
    } else {
      this.inputElement = document.createElement('input')
      this.inputElement.type = this.getInputType()
      this.inputElement.value = this.formatForInput(this.currentValue)
      this.inputElement.className = 'w-full bg-white border-2 border-blue-400 rounded px-2 py-1 text-sm text-right focus:outline-none focus:border-blue-600'
    }

    // Event listeners
    this.inputElement.addEventListener('blur', this.saveValue.bind(this))
    this.inputElement.addEventListener('keydown', this.handleKeydown.bind(this))
    if (this.fieldTypeValue !== 'select') {
      this.inputElement.addEventListener('input', this.validateInput.bind(this))
    }

    this.displayTarget.appendChild(this.inputElement)
  }

  getInputType() {
    switch(this.fieldTypeValue) {
      case 'currency':
      case 'rate':
      case 'integer':
        return 'text' // Use text to allow formatting
      case 'text':
        return 'text'
      case 'select':
        return 'select' // This won't be used since we create a select element
      default:
        return 'text'
    }
  }

  formatForInput(value) {
    if (value === null || value === undefined || value === '') return ''

    switch(this.fieldTypeValue) {
      case 'currency':
      case 'rate':
        // Show numbers without currency symbols for editing
        return typeof value === 'number' ? value.toString() : value.toString()
      case 'integer':
        return typeof value === 'number' ? Math.round(value).toString() : value.toString()
      case 'text':
        return value.toString()
      case 'select':
        return value.toString() // For selects, the value is set directly on the option
      default:
        return value.toString()
    }
  }

  handleKeydown(event) {
    switch(event.key) {
      case 'Enter':
        this.saveValue()
        break
      case 'Escape':
        this.cancelEdit()
        break
      case 'Tab':
        this.saveValue()
        // Let tab continue to next cell
        break
    }
  }

  validateInput(event) {
    // Real-time validation as user types
    const value = event.target.value

    if (this.fieldTypeValue === 'currency' || this.fieldTypeValue === 'rate') {
      // Allow digits, decimal point, and negative sign
      const validPattern = /^-?\d*\.?\d*$/
      if (!validPattern.test(value)) {
        event.target.value = value.slice(0, -1) // Remove last character
      }
    } else if (this.fieldTypeValue === 'integer') {
      // Allow only digits and negative sign
      const validPattern = /^-?\d*$/
      if (!validPattern.test(value)) {
        event.target.value = value.slice(0, -1) // Remove last character
      }
    }
  }

  saveValue() {
    if (!this.isEditing) return

    const rawValue = this.inputElement.value
    const processedValue = this.processInputValue(rawValue)

    if (this.isValidValue(processedValue)) {
      this.currentValue = processedValue
      this.updateDisplay()
      this.markAsChanged()

      // Notify sheet controller of change
      if (this.sheetController) {
        this.sheetController.cellValueChanged(this.fieldPathValue, processedValue)
      }
    } else {
      this.showValidationError()
      return // Keep editing
    }

    this.finishEditing()
  }

  cancelEdit() {
    if (!this.isEditing) return

    this.finishEditing()
  }

  processInputValue(rawValue) {
    if (rawValue === '' || rawValue === null || rawValue === undefined) {
      return null
    }

    switch(this.fieldTypeValue) {
      case 'currency':
      case 'rate':
        // Remove currency symbols, parse as float
        const numValue = parseFloat(rawValue.replace(/[$,]/g, ''))
        return isNaN(numValue) ? null : numValue
      case 'integer':
        const intValue = parseInt(rawValue.replace(/[,]/g, ''))
        return isNaN(intValue) ? null : intValue
      case 'text':
        return rawValue.trim()
      case 'select':
        return rawValue.trim()
      default:
        return rawValue
    }
  }

  isValidValue(value) {
    switch(this.fieldTypeValue) {
      case 'currency':
      case 'rate':
        return value === null || (typeof value === 'number' && !isNaN(value))
      case 'integer':
        return value === null || (typeof value === 'number' && Number.isInteger(value))
      case 'text':
        return true // All text values are valid
      case 'select':
        return true // All select values are valid (they come from predefined options)
      default:
        return true
    }
  }

  updateDisplay() {
    const formattedValue = this.formatForDisplay(this.currentValue)
    const valueDisplay = this.displayTarget.querySelector('.value-display')
    if (valueDisplay) {
      valueDisplay.textContent = formattedValue
    }
  }

  formatForDisplay(value) {
    if (value === null || value === undefined || value === '') return ''

    switch(this.fieldTypeValue) {
      case 'currency':
        return this.formatCurrency(value)
      case 'rate':
        const precision = this.precisionValue || 3
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: precision,
          maximumFractionDigits: precision
        }).format(value)
      case 'integer':
        return new Intl.NumberFormat('en-US').format(value)
      case 'text':
        return value.toString()
      case 'select':
        return value.toString()
      default:
        return value.toString()
    }
  }

  formatCurrency(value) {
    const precision = this.precisionValue || 2
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: precision,
      maximumFractionDigits: precision
    }).format(value)
  }

  showInput() {
    // Hide the display content and show input
    const valueDisplay = this.displayTarget.querySelector('.value-display')
    if (valueDisplay) {
      valueDisplay.style.display = 'none'
    }
    this.inputElement.style.display = 'block'
  }

  hideInput() {
    // Show the display content and hide input
    const valueDisplay = this.displayTarget.querySelector('.value-display')
    if (valueDisplay) {
      valueDisplay.style.display = 'inline'
    }
    if (this.inputElement) {
      this.inputElement.remove()
      this.inputElement = null
    }
  }

  finishEditing() {
    this.isEditing = false
    this.element.classList.remove('editing')
    this.hideInput()
  }

  markAsChanged() {
    if (this.currentValue !== this.originalValueValue) {
      this.element.classList.add('has-changes', 'bg-yellow-50', 'border-l-4', 'border-orange-400')

      // Add change indicator dot
      //if (!this.element.querySelector('.change-indicator')) {
      //  const indicator = document.createElement('div')
      //  indicator.className = 'change-indicator absolute top-1 right-1 w-2 h-2 bg-orange-400 rounded-full'
      //  this.element.style.position = 'relative'
      //  this.element.appendChild(indicator)
      //}
    } else {
      this.element.classList.remove('has-changes', 'bg-yellow-50', 'border-l-4', 'border-orange-400')
      const indicator = this.element.querySelector('.change-indicator')
      if (indicator) {
        indicator.remove()
      }
    }
  }

  restoreOriginalValue() {
    this.currentValue = this.originalValueValue
    this.updateDisplay()
    this.element.classList.remove('has-changes', 'bg-yellow-50', 'border-l-4', 'border-orange-400')
    const indicator = this.element.querySelector('.change-indicator')
    if (indicator) {
      indicator.remove()
    }
  }

  showValidationError() {
    // Flash red border briefly
    this.inputElement.classList.add('border-red-500')
    setTimeout(() => {
      this.inputElement.classList.remove('border-red-500')
      this.inputElement.classList.add('border-blue-400')
    }, 1000)
  }

  findSheetController() {
    const sheetElement = this.element.closest('[data-controller*="editable-rate-sheet"]')
    if (sheetElement) {
      return this.application.getControllerForElementAndIdentifier(sheetElement, 'editable-rate-sheet')
    }
    return null
  }
}
